// note.ts — CTSK-509. Lightweight "post-it" notes.
//
// One primitive, three target shapes: a document, a work item, or standalone
// (no target). Markdown body; fixed 5-colour palette; author-owned.
//
// The palette here MUST match the CHECK constraint in migration 0071
// (apps/api/src/db/migrations/0071_notes.sql). apps/api/src/db/migration-0071.test.ts
// asserts that, so a sixth colour added here without a new migration fails a
// test instead of failing inserts in production.
import { z } from 'zod';
export const NOTE_COLOR_PALETTE = ['yellow', 'pink', 'blue', 'green', 'gray'];
export const NOTE_DEFAULT_COLOR = 'yellow';
export const NOTE_TARGET_TYPES = ['document', 'workitem'];
/** Body cap: keeps the post-it feel. Mirrors the CHECK in migration 0071. */
export const NOTE_BODY_MAX = 4000;
/** The web editor shows a character counter once the body passes this. */
export const NOTE_COUNTER_THRESHOLD = 3500;
/** Hard cap on one list response (the panel shows "latest 100"). */
export const NOTE_LIST_MAX = 100;
/** target_id column is varchar(64) (documents ids are the widest target). */
export const NOTE_TARGET_ID_MAX = 64;
// CTSK-514 — saved card height (px). Mirrors notes_height_px_check in migration 0073.
export const NOTE_HEIGHT_MIN = 80;
export const NOTE_HEIGHT_MAX = 1200;
// CTSK-514 — /notes wall view preferences. These lists MUST match the CHECKs
// on note_prefs in migration 0073 (asserted by migration-0073.test.ts).
export const NOTE_ORDER_MODES = ['manual', 'sorted'];
export const NOTE_SORT_KEYS = ['updated', 'created', 'color', 'title'];
export const NOTE_SORT_DIRS = ['asc', 'desc'];
export const NOTE_GRID_TYPES = ['masonry', 'grid', 'list'];
export const NOTE_PREFS_DEFAULT = {
    orderMode: 'sorted',
    sortKey: 'updated',
    sortDir: 'desc',
    gridType: 'masonry',
};
export const NoteColorEnum = z.enum(NOTE_COLOR_PALETTE);
export const NoteTargetTypeEnum = z.enum(NOTE_TARGET_TYPES);
/** A body must carry at least one visible character (the DB only checks length >= 1). */
const NoteBodySchema = z
    .string()
    .min(1)
    .max(NOTE_BODY_MAX)
    .refine((s) => s.trim().length > 0, { message: 'body must not be blank' });
/**
 * Output shape. `authorDisplayName` and `targetTitle` are read-time joins
 * (initials on the card; the "Attached to: <title>" link) — they are not
 * columns. `targetTitle` is null for standalone notes and for targets the
 * caller can no longer see.
 */
export const NoteSchema = z.object({
    id: z.string(),
    orgId: z.string(),
    authorId: z.string(),
    authorDisplayName: z.string().nullable().optional(),
    body: z.string(),
    color: NoteColorEnum,
    targetType: NoteTargetTypeEnum.nullable(),
    targetId: z.string().nullable(),
    targetTitle: z.string().nullable().optional(),
    createdAt: z.string(),
    updatedAt: z.string(),
    deletedAt: z.string().nullable().optional(),
    /** CTSK-514 — saved height in px; null/absent = fit to content. */
    heightPx: z.number().int().nullable().optional(),
});
/** targetType + targetId are both set or both omitted (standalone). */
function refineTargetPair(v, ctx) {
    const hasType = v.targetType !== undefined;
    const hasId = v.targetId !== undefined;
    if (hasType !== hasId) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [hasType ? 'targetId' : 'targetType'],
            message: 'targetType and targetId must be provided together (or both omitted for a standalone note)',
        });
    }
}
/** Strict: unknown keys are rejected with 400 (CTSK-445 class-fix), not silently stripped. */
export const NoteCreateInputSchema = z
    .object({
    body: NoteBodySchema,
    color: NoteColorEnum.default(NOTE_DEFAULT_COLOR),
    targetType: NoteTargetTypeEnum.optional(),
    targetId: z.string().min(1).max(NOTE_TARGET_ID_MAX).optional(),
})
    .strict()
    .superRefine(refineTargetPair);
/** Strict. Both fields optional; a note's target is immutable. */
export const NoteHeightSchema = z.number().int().min(NOTE_HEIGHT_MIN).max(NOTE_HEIGHT_MAX);
export const NoteUpdateInputSchema = z
    .object({
    body: NoteBodySchema.optional(),
    color: NoteColorEnum.optional(),
    /** CTSK-514 — a pixel height, or null to fit to content. Never bumps updatedAt. */
    heightPx: NoteHeightSchema.nullable().optional(),
})
    .strict();
export const NotePrefsSchema = z
    .object({
    orderMode: z.enum(NOTE_ORDER_MODES),
    sortKey: z.enum(NOTE_SORT_KEYS),
    sortDir: z.enum(NOTE_SORT_DIRS),
    gridType: z.enum(NOTE_GRID_TYPES),
})
    .strict();
/** `PUT /notes/order` — put `noteId` right before `beforeId` OR right after `afterId` (exactly one). */
export const NoteOrderInputSchema = z
    .object({
    noteId: z.string().min(1).max(26),
    beforeId: z.string().min(1).max(26).optional(),
    afterId: z.string().min(1).max(26).optional(),
    /**
     * The order the client is currently DISPLAYING (note ids). Sent when the wall
     * is in Sorted mode, so the first drag ranks exactly what the user saw before
     * applying the move. Unknown ids are ignored; notes not listed keep their
     * relative manual order after the listed ones.
     */
    basis: z.array(z.string().min(1).max(26)).max(1000).optional(),
})
    .strict()
    .superRefine((v, ctx) => {
    const n = (v.beforeId !== undefined ? 1 : 0) + (v.afterId !== undefined ? 1 : 0);
    if (n !== 1) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['beforeId'], message: 'provide exactly one of beforeId or afterId' });
    }
    if (v.beforeId === v.noteId || v.afterId === v.noteId) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['noteId'], message: 'a note cannot be placed relative to itself' });
    }
});
// `GET /notes` query. Exactly one of `target` / `mine`.
//   target=document:<id> | target=workitem:<id>
//   mine=true            (the caller's own standalone notes)
//   deletedOnly=true     (CTSK-513 — Recycle Bin: every soft-deleted note in the
//                         org; additionally requires `admin:view_deleted`)
// `limit` is CLAMPED into 1..NOTE_LIST_MAX (default NOTE_LIST_MAX), never rejected.
const NOTE_TARGET_QUERY_RE = /^(document|workitem):(.+)$/;
export function parseNoteTarget(raw) {
    const m = NOTE_TARGET_QUERY_RE.exec(raw);
    if (!m)
        return null;
    const targetId = m[2];
    if (targetId.length > NOTE_TARGET_ID_MAX)
        return null;
    return { targetType: m[1], targetId };
}
const clampLimit = (v) => {
    if (v === undefined || v === null || v === '')
        return NOTE_LIST_MAX;
    const n = typeof v === 'number' ? v : Number(v);
    if (!Number.isFinite(n))
        return NOTE_LIST_MAX;
    return Math.min(NOTE_LIST_MAX, Math.max(1, Math.trunc(n)));
};
export const NoteListQuerySchema = z
    .object({
    target: z
        .string()
        .refine((s) => parseNoteTarget(s) !== null, {
        message: 'target must be document:<id> or workitem:<id>',
    })
        .optional(),
    mine: z.enum(['true']).optional(),
    deletedOnly: z.enum(['true']).optional(),
    // CTSK-514 — ordering for `mine=true` only. `order=manual` = the caller's
    // own drag order; `sort`/`dir` = a column sort. Mutually exclusive. With
    // either present the cursor is an offset cursor; without, keyset as before.
    order: z.enum(['manual']).optional(),
    sort: z.enum(NOTE_SORT_KEYS).optional(),
    dir: z.enum(NOTE_SORT_DIRS).optional(),
    limit: z.preprocess(clampLimit, z.number().int().min(1).max(NOTE_LIST_MAX)),
    cursor: z.string().min(1).max(300).optional(),
})
    .strict()
    .superRefine((v, ctx) => {
    const n = (v.target !== undefined ? 1 : 0) +
        (v.mine !== undefined ? 1 : 0) +
        (v.deletedOnly !== undefined ? 1 : 0);
    if (n !== 1) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['target'],
            message: 'provide exactly one of target=<document|workitem>:<id>, mine=true or deletedOnly=true',
        });
    }
    const ordering = v.order !== undefined || v.sort !== undefined || v.dir !== undefined;
    if (ordering && v.mine === undefined) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['order'], message: 'order, sort and dir are only valid with mine=true' });
    }
    if (v.order !== undefined && (v.sort !== undefined || v.dir !== undefined)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['order'], message: 'order=manual cannot be combined with sort or dir' });
    }
    if (v.dir !== undefined && v.sort === undefined && v.order === undefined) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['dir'], message: 'dir requires sort' });
    }
});
//# sourceMappingURL=note.js.map